import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import Footer from './Footer';

const FeatureCard = ({ title, description, icon, gradient }) => (
  <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-1">
    <div className={`bg-gradient-to-br ${gradient} rounded-2xl p-8 h-full`}>
      <div className="flex flex-col h-full">
        <div className="flex items-start gap-4 mb-6">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
              {icon}
            </div>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
          </div>
        </div>
        <p className="text-gray-300">{description}</p>
      </div>
    </div>
  </div>
);

const Features = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      title: "Natural Language Understanding",
      description: "Our AI agents understand context, nuance, and intent in human conversations, enabling more meaningful and accurate interactions with your customers.",
      icon: (
        <svg className="w-6 h-6 text-[#FF56A5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      ),
      gradient: "from-[#FF56A5]/20 via-[#E852FF]/20 to-[#9F44F2]/20"
    },
    {
      title: "Multi-Channel Integration",
      description: "Seamlessly deploy agents across email, chat, SMS, and voice channels while maintaining consistent conversation history and context.",
      icon: (
        <svg className="w-6 h-6 text-[#E852FF]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      gradient: "from-[#E852FF]/20 via-[#9F44F2]/20 to-[#FF56A5]/20"
    },
    {
      title: "Automated Scheduling",
      description: "Intelligent calendar management and scheduling capabilities that handle time zones, availability, and meeting preferences automatically.",
      icon: (
        <svg className="w-6 h-6 text-[#9F44F2]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      gradient: "from-[#9F44F2]/20 via-[#FF56A5]/20 to-[#E852FF]/20"
    },
    {
      title: "Custom Workflows",
      description: "Design and implement custom conversation flows and business logic to match your specific use cases and requirements.",
      icon: (
        <svg className="w-6 h-6 text-[#FF56A5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
        </svg>
      ),
      gradient: "from-[#FF56A5]/20 via-[#E852FF]/20 to-[#9F44F2]/20"
    },
    {
      title: "Real-time Analytics",
      description: "Comprehensive analytics dashboard providing insights into conversation metrics, agent performance, and customer satisfaction.",
      icon: (
        <svg className="w-6 h-6 text-[#E852FF]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      gradient: "from-[#E852FF]/20 via-[#9F44F2]/20 to-[#FF56A5]/20"
    },
    {
      title: "Enterprise Security",
      description: "Bank-grade security with end-to-end encryption, SOC 2 compliance, and advanced data protection measures to keep your conversations secure.",
      icon: (
        <svg className="w-6 h-6 text-[#9F44F2]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      gradient: "from-[#9F44F2]/20 via-[#FF56A5]/20 to-[#E852FF]/20"
    }
  ];

  return (
    <div className="landing-page bg-background min-h-screen">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Powerful{' '}
            <span className="bg-gradient-to-r from-[#FF56A5] via-[#E852FF] to-[#9F44F2] bg-clip-text text-transparent">
              Features
            </span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Discover the advanced capabilities that make Lifelike Agents the leading choice for AI-powered communication
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {features.map((feature, index) => (
            <FeatureCard key={index} {...feature} />
          ))}
        </div>

        {/* CTA Section */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-1">
            <div className="bg-gradient-to-br from-[#FF56A5]/20 via-[#E852FF]/20 to-[#9F44F2]/20 rounded-2xl p-8 text-center">
              <h2 className="text-2xl font-semibold text-white mb-4">
                Ready to Experience These Features?
              </h2>
              <p className="text-gray-300 mb-6">
                See how our powerful features can transform your business communication.
              </p>
              <Link
                to="/request-demo"
                className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-full text-white bg-gradient-to-r from-[#FF56A5] via-[#E852FF] to-[#9F44F2] hover:from-[#FF56A5]/90 hover:via-[#E852FF]/90 hover:to-[#9F44F2]/90 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Request a Demo
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Features; 